import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { VIBE_PROGRESS_STEPS } from '../../../features/vibe-coding/redux/constants';

export const STATUS_ICONS = {
  completed: { icon: '✓', color: '#16a34a' },
  running: { icon: '●', color: '#303453' },
  failed: { icon: '✕', color: '#dc2626' },
  stopped: { icon: '✕', color: '#dc2626' },
  pending: { icon: '○', color: '#a0aec0' },
};

export const RunningSpinner = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: 'vibeSpin 1s linear infinite' }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke="#e2e8f0" strokeWidth="3" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const TodoBox = ({ checked, failed }) => (
  <span
    aria-hidden="true"
    className="d-inline-flex align-items-center justify-content-center"
    style={{
      width: '18px',
      height: '18px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: 1,
      color: '#ffffff',
      background: checked ? '#16a34a' : failed ? '#dc2626' : 'transparent',
      border: checked || failed ? '1px solid transparent' : '1.5px solid #cbd5e1',
      flexShrink: 0,
    }}
  >
    {checked ? '✓' : failed ? '✕' : null}
  </span>
);

TodoBox.propTypes = {
  checked: PropTypes.bool,
  failed: PropTypes.bool,
};

TodoBox.defaultProps = {
  checked: false,
  failed: false,
};

export const getTodoIcon = (status) => {
  if (status === 'running') return <RunningSpinner />;
  if (status === 'completed') return <TodoBox checked />;
  if (status === 'failed' || status === 'stopped') return <TodoBox failed />;
  return <TodoBox />;
};

export const getBuildStepsState = (milestones, isRunning) => {
  const steps = Array.isArray(milestones) ? milestones : [];
  const completedSteps = steps.filter((step) => step.status === 'completed').length;
  const progressPercent = steps.length
    ? Math.min(100, Math.round((completedSteps / steps.length) * 100))
    : 0;

  const hasFailed = steps.some((step) => step.status === 'failed' || step.status === 'stopped');
  const runningIndex = steps.findIndex((step) => step.status === 'running');
  const lastCompletedIndex = steps.reduce(
    (last, step, idx) => (step.status === 'completed' ? idx : last),
    -1,
  );

  const isRework = runningIndex !== -1 && lastCompletedIndex > runningIndex;
  return {
    steps,
    completedSteps,
    progressPercent,
    hasFailed,
    runningIndex,
    isRework,
    reworkStep: isRework ? steps[runningIndex] : null,
    statusColor: hasFailed ? '#dc2626' : isRunning ? '#2563eb' : '#16a34a',
    statusText: hasFailed ? 'Build paused' : isRunning ? 'Building your app...' : 'Build finished',
    activeIndex:
      runningIndex !== -1 ? runningIndex : steps.findIndex((step) => step.status === 'pending'),
    allDone: steps.length > 0 && completedSteps === steps.length && runningIndex === -1,
  };
};

export const getStepRowStyle = ({ isLive, isDone, isFailed, isRework }) => ({
  background: isLive ? (isRework ? '#fffbeb' : '#eff6ff') : 'transparent',
  border: isLive ? `1px solid ${isRework ? '#fcd34d' : '#bfdbfe'}` : '1px solid transparent',
  color: isDone || isLive ? '#1e293b' : '#94a3b8',
  fontSize: '13px',
  fontWeight: isLive ? 600 : 400,
  opacity: !isLive && !isDone && !isFailed ? 0.75 : 1,
});

export const getLiveBadgeStyle = (isRework) => ({
  fontSize: '10px',
  background: isRework ? '#fef3c7' : '#dbeafe',
  color: isRework ? '#92400e' : '#1d4ed8',
  border: `1px solid ${isRework ? '#fcd34d' : '#bfdbfe'}`,
});

export const useBudgetCountdown = (budget, isActive) => {
  const [remainingMs, setRemainingMs] = useState(null);
  const deadlineRef = useRef(null);
  const syncKeyRef = useRef(null);

  useEffect(() => {
    if (!isActive || !budget || typeof budget.remainingMs !== 'number') {
      deadlineRef.current = null;
      syncKeyRef.current = null;
      setRemainingMs(null);
      return undefined;
    }

    const syncKey = `${budget.remainingMs}:${budget.elapsedMs || 0}`;
    if (syncKeyRef.current !== syncKey) {
      syncKeyRef.current = syncKey;
      deadlineRef.current = Date.now() + budget.remainingMs;
      setRemainingMs(budget.remainingMs);
    }

    const tick = () => {
      if (!deadlineRef.current) return;
      setRemainingMs(Math.max(0, deadlineRef.current - Date.now()));
    };

    const intervalId = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(intervalId);
  }, [isActive, budget]);

  return remainingMs;
};

export const createInitialMilestones = () =>
  VIBE_PROGRESS_STEPS.map((step) => ({ ...step, status: 'pending' }));

const STEP_ORDER = VIBE_PROGRESS_STEPS.map((step) => step.id);

const taskIdToStep = (taskId) => {
  const id = String(taskId || '');
  if (/brand|design|page|ui/i.test(id)) return 'build_ui';
  if (/data|collection|domain/i.test(id)) return 'connect_data';
  if (/interaction|event|flow/i.test(id)) return 'interactions';
  return null;
};

const eventStepId = (event) => {
  const fromTask = taskIdToStep(event.taskId);
  if (fromTask) return fromTask;
  const phase = event.name;
  if (['classifying', 'planning', 'plan', 'resuming'].includes(phase)) return 'understand_plan';
  if (['initialized', 'blueprint'].includes(phase)) return 'prepare_workspace';
  if (['customize', 'generating'].includes(phase)) return 'build_ui';
  if (['verify', 'verifying', 'repair', 'finalize'].includes(phase)) return 'verify';
  return null;
};

const completePriorSteps = (milestones, stepId) => {
  const index = STEP_ORDER.indexOf(stepId);
  if (index <= 0) return milestones;
  return milestones.map((step) => {
    const stepIndex = STEP_ORDER.indexOf(step.id);
    if (stepIndex >= 0 && stepIndex < index && step.status !== 'completed') {
      return { ...step, status: 'completed' };
    }
    return step;
  });
};

const applyCompletedTaskIds = (milestones, taskIds) => {
  const ids = Array.isArray(taskIds) ? taskIds : [];
  if (!ids.length) return milestones;
  const stepIds = new Set(ids.map(taskIdToStep).filter(Boolean));
  if (stepIds.size) {
    stepIds.add('understand_plan');
    stepIds.add('prepare_workspace');
  }
  return milestones.map((step) =>
    stepIds.has(step.id) && step.status !== 'completed' ? { ...step, status: 'completed' } : step,
  );
};

export const updateMilestones = (milestones, event) => {
  const current =
    Array.isArray(milestones) && milestones.length ? milestones : createInitialMilestones();
  if (event.type === 'completed') return current.map((step) => ({ ...step, status: 'completed' }));
  if (event.type === 'failed' || event.type === 'stopped') {
    return applyCompletedTaskIds(
      current.map((step) => (step.status === 'running' ? { ...step, status: event.type } : step)),
      event.completedTaskIds,
    );
  }
  let next = applyCompletedTaskIds(current, event.completedTaskIds);
  const stepId = eventStepId(event);
  if (!stepId) return next;
  next = completePriorSteps(next, stepId);
  const status = event.type === 'task_completed' ? 'completed' : 'running';
  return next.map((step) =>
    step.id === stepId ? { ...step, status, elapsedMs: event.elapsedMs || step.elapsedMs } : step,
  );
};
